import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowLeft, Share2, Copy, Check, FileSignature, Brain, ShieldCheck,
  ArrowRight, Zap, ScanLine, Pill,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/prescription-platform';
const SHARE_MESSAGE = (
  `DocSite just launched the biggest platform update since launch — a complete rewrite of how prescriptions are written, delivered, and verified in India.\n\n` +
  `1. AUTO-PRESCRIPTIONS — every Digital Health File auto-issues a signed, pharmacy-ready A4 PDF. Zero extra clicks.\n\n` +
  `2. SMART MEDICINE COMPOSER — AI fills molecule + drug class + schedule + strength from just the brand name. Your dosing habits per brand are remembered. Total quantity auto-computed.\n\n` +
  `3. PUBLIC VERIFY TOOL — any pharmacist can drop a DocSite Rx PDF at docsite.care/rx-verify and see instantly whether it is genuine.\n\n` +
  `Together, this is the DocSite Prescription Platform. Read more: ${SHARE_URL}`
);

const PILLARS = [
  {
    icon: FileSignature, tag: 'Pillar 1',
    title: 'Auto-Prescriptions',
    body: 'Seal a Digital Health File → a fully-signed A4 prescription is auto-generated and delivered inside the same patient link. No extra clicks. No paper. No lost slips.',
    href: '/whats-new/auto-prescriptions',
    color: 'sky',
  },
  {
    icon: Brain, tag: 'Pillar 2',
    title: 'Smart Medicine Composer',
    body: 'Type a brand name — AI auto-fills the molecule, drug class, regulatory schedule and default strength. Your personal medicine memory learns your dosing habits per brand. Quantity auto-computed.',
    href: '/whats-new/smart-medicine-composer',
    color: 'violet',
  },
  {
    icon: ShieldCheck, tag: 'Pillar 3',
    title: 'DocSite Verified Rx',
    body: 'A public tool at docsite.care/rx-verify. Any pharmacist, insurer or patient can drop a Rx PDF and see instantly whether it is genuine, revoked, or unknown. Cryptographic SHA-256 fingerprint check.',
    href: '/whats-new/docsite-verified-rx',
    color: 'emerald',
  },
];

const CTA_COLORS = {
  sky:     { bg: 'from-sky-50 via-white to-sky-100', border: 'border-sky-200', pill: 'bg-sky-600', dot: 'text-sky-700', head: 'text-sky-900' },
  violet:  { bg: 'from-violet-50 via-white to-violet-100', border: 'border-violet-200', pill: 'bg-violet-600', dot: 'text-violet-700', head: 'text-violet-900' },
  emerald: { bg: 'from-emerald-50 via-white to-emerald-100', border: 'border-emerald-200', pill: 'bg-emerald-600', dot: 'text-emerald-700', head: 'text-emerald-900' },
};

const NUMBERS = [
  { n: '12',  label: 'Pharmacy form types one-tap: TAB, CAP, SYRUP, INJ, DROP, OINTMENT·GEL, OINTMENT·CREAM, SPRAY, GARGLE, RINSE SACHETS, WASH, IV' },
  { n: '9',   label: 'Structured columns on every printed Rx: brand, molecule, strength, dose+frequency, duration, instructions, quantity, drug class, schedule' },
  { n: '256', label: 'Bits of SHA-256 cryptographic proof stamped on every prescription — same evidentiary weight as Aadhaar e-Sign' },
  { n: '1',   label: 'Click to seal a Digital Health File and issue the prescription. Delivered via WhatsApp / SMS in the same link.' },
];

export default function PrescriptionPlatformLaunch() {
  const [copied, setCopied] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(SHARE_URL); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard denied */ }
  };

  return (
    <div className="min-h-screen bg-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="platform-launch-page">
      <Helmet>
        <title>The DocSite Prescription Platform — Auto-Rx + AI + Public Verify | DocSite</title>
        <meta name="description" content="A three-part platform update: auto-prescriptions from sealed DHFs, AI-powered smart medicine composer, and a public verification tool. Together, the DocSite Prescription Platform." />
        <meta property="og:title" content="DocSite — The Prescription Platform" />
        <meta property="og:description" content="Complete rewrite of how doctors write, and pharmacists verify, prescriptions in India." />
        <meta property="og:url" content={SHARE_URL} />
        <meta property="og:image" content="https://docsite.care/auto-rx-launch-preview.png" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-5 py-10">
        <Link to="/whats-new" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-6" data-testid="back-to-whats-new">
          <ArrowLeft size={12} /> All updates
        </Link>

        <div className="rounded-3xl bg-gradient-to-br from-sky-100 via-white to-violet-100 border-2 border-sky-300 p-8 sm:p-12 mb-8 relative overflow-hidden">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-violet-200 opacity-30" aria-hidden="true"></div>
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-emerald-200 opacity-30" aria-hidden="true"></div>
          <div className="relative">
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full bg-slate-900 text-white">
              <Sparkles size={11} /> Platform Launch · 4 Aug 2026
            </span>
            <h1 className="text-3xl sm:text-5xl font-bold text-slate-900 mt-6 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              The DocSite Prescription Platform.
            </h1>
            <p className="text-xl sm:text-2xl font-semibold text-slate-700 mt-3 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              A complete rewrite of how doctors write — and pharmacists verify — prescriptions in India.
            </p>
            <p className="mt-5 text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl">
              Three big launches shipped in a single day. Individually, each one saves a doctor time. Together, they
              retire the paper prescription entirely — in every clinic that runs on DocSite. From today, an entire
              consultation from history-taking to a pharmacy-verified prescription takes fewer keystrokes than one paper Rx.
            </p>
          </div>
        </div>

        {/* Three pillars */}
        <div className="grid gap-4 sm:grid-cols-3 mb-8" data-testid="pillars-grid">
          {PILLARS.map((p, idx) => {
            const c = CTA_COLORS[p.color];
            const Ico = p.icon;
            return (
              <Link to={p.href} key={idx} data-testid={`pillar-${idx}`}
                    className={`rounded-2xl bg-gradient-to-br ${c.bg} border-2 ${c.border} p-5 flex flex-col hover:shadow-md transition-shadow group`}>
                <span className={`self-start inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${c.pill} text-white`}>
                  {p.tag}
                </span>
                <Ico size={30} className={`${c.dot} mt-4`} strokeWidth={1.75} />
                <h3 className={`text-lg font-bold ${c.head} mt-3 leading-tight`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {p.title}
                </h3>
                <p className="text-sm text-slate-700 mt-2 leading-relaxed flex-1">{p.body}</p>
                <span className={`mt-4 inline-flex items-center gap-1 text-xs font-bold ${c.dot} group-hover:translate-x-0.5 transition-transform`}>
                  Read the 1-pager <ArrowRight size={13} />
                </span>
              </Link>
            );
          })}
        </div>

        {/* By the numbers */}
        <div className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 mb-8" data-testid="by-the-numbers">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">By the numbers</p>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            The Prescription Platform in four numbers.
          </h2>
          <div className="grid sm:grid-cols-2 gap-5 mt-6">
            {NUMBERS.map((n, idx) => (
              <div key={idx} className="flex gap-4 items-start" data-testid={`stat-${idx}`}>
                <div className="text-4xl sm:text-5xl font-black text-slate-900 leading-none shrink-0" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {n.n}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed pt-1">{n.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Why this matters */}
        <div className="rounded-3xl bg-slate-900 text-white p-6 sm:p-10 mb-8">
          <p className="text-[10px] font-bold uppercase tracking-widest text-sky-300">Why this matters</p>
          <h2 className="text-2xl sm:text-3xl font-semibold mt-2 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            India writes an estimated 3 billion prescriptions a year — almost all on paper.
          </h2>
          <p className="mt-4 text-base text-slate-300 leading-relaxed">
            Paper prescriptions get lost. Get faked. Get misread. Take doctors 45+ seconds per patient. Cost pharmacies
            trust and revenue. Cost insurers auditability. Cost patients — who cannot even show a genuine copy to a
            second-opinion doctor six months later.
          </p>
          <p className="mt-4 text-base text-slate-300 leading-relaxed">
            The DocSite Prescription Platform fixes every one of those failures — in a single release, with zero learning
            curve for the doctor. Type a brand name. Everything else auto-fills. Seal the DHF. Everything else auto-delivers.
          </p>
          <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
            <div className="rounded-xl bg-white/5 border border-white/10 p-4">
              <Zap size={18} className="text-sky-400 mb-2" />
              <p className="font-bold text-white">Faster than paper</p>
              <p className="text-slate-400 mt-1 text-xs leading-relaxed">One click seals DHF + issues Rx + delivers via WhatsApp.</p>
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-4">
              <ScanLine size={18} className="text-emerald-400 mb-2" />
              <p className="font-bold text-white">Verifiable by anyone</p>
              <p className="text-slate-400 mt-1 text-xs leading-relaxed">QR on every Rx. Pharmacists scan, get an instant genuine/revoked verdict.</p>
            </div>
            <div className="rounded-xl bg-white/5 border border-white/10 p-4">
              <Pill size={18} className="text-violet-400 mb-2" />
              <p className="font-bold text-white">Smarter over time</p>
              <p className="text-slate-400 mt-1 text-xs leading-relaxed">AI + your medicine memory means every subsequent Rx is faster than the last.</p>
            </div>
          </div>
        </div>

        {/* Share */}
        <div className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-2 border-emerald-200 p-6 sm:p-8">
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Spread the word</p>
          <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 mt-2 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            If you know a clinic still buying prescription pads — send them this.
          </h3>
          <p className="mt-3 text-sm text-slate-700">
            The whole prescription platform is free for every DocSite doctor from today. No plan upgrade needed. Just seal a DHF and it works.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-sm font-bold" data-testid="share-whatsapp">
              <Share2 size={14} /> Share on WhatsApp
            </a>
            <button onClick={copyLink}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white hover:bg-slate-50 text-slate-900 text-sm font-bold border border-slate-300" data-testid="share-copy">
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Link copied' : 'Copy link'}
            </button>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-10">Published 4 August 2026 · DocSite Prescription Platform v1</p>
      </div>
    </div>
  );
}
